import { useState, type FormEvent } from 'react';
import { Clock3, Pencil, Plus, Power } from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmModal from '../ConfirmModal.tsx';
import {
  createAgendaService,
  setAgendaServiceActive,
  updateAgendaService,
} from '../../services/api.ts';
import type { AgendaService } from '../../types';

type ServicesPanelProps = {
  services: AgendaService[];
  onChanged: () => Promise<void> | void;
};

const emptyForm = {
  name: '',
  description: '',
  durationMinutes: 30,
  price: '',
  color: '#322F84',
};

const ServicesPanel = ({ services, onChanged }: ServicesPanelProps) => {
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<AgendaService | null>(null);

  const startEditing = (service: AgendaService) => {
    setEditingId(service.id);
    setForm({
      name: service.name,
      description: service.description || '',
      durationMinutes: service.durationMinutes,
      price: service.price === null ? '' : String(service.price),
      color: service.color,
    });
  };

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const price = form.price === '' ? null : Number(String(form.price).replace(',', '.'));
    if (price !== null && (!Number.isFinite(price) || price < 0)) {
      toast.error('Informe um preço válido ou deixe o campo vazio.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        durationMinutes: Number(form.durationMinutes),
        price,
        color: form.color,
      };
      if (editingId) await updateAgendaService(editingId, payload);
      else await createAgendaService(payload);
      toast.success(editingId ? 'Serviço atualizado.' : 'Serviço criado.');
      reset();
      await onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível salvar o serviço.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!toggleTarget) return;
    setSaving(true);
    try {
      await setAgendaServiceActive(toggleTarget.id, !toggleTarget.active);
      toast.success(toggleTarget.active ? 'Serviço desativado.' : 'Serviço reativado.');
      setToggleTarget(null);
      await onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível alterar o serviço.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agenda-settings-layout">
      <section className="agenda-panel">
        <div className="agenda-section-heading">
          <div>
            <span className="agenda-eyebrow"><Plus size={15} /> Catálogo</span>
            <h2>{editingId ? 'Editar serviço' : 'Novo serviço'}</h2>
            <p>Defina a duração usada para calcular automaticamente cada agendamento.</p>
          </div>
        </div>
        <form className="agenda-form" onSubmit={handleSubmit}>
          <div className="agenda-form-grid">
            <label>
              <span>Nome *</span>
              <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} maxLength={120} required />
            </label>
            <label>
              <span>Duração *</span>
              <input
                type="number"
                min={5}
                max={480}
                step={5}
                value={form.durationMinutes}
                onChange={(event) => setForm({ ...form, durationMinutes: Number(event.target.value) })}
                required
              />
            </label>
            <label>
              <span>Preço</span>
              <input inputMode="decimal" value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} placeholder="Opcional" aria-label="Preço opcional" />
            </label>
            <label>
              <span>Cor</span>
              <input type="color" value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} />
            </label>
          </div>
          <label>
            <span>Descrição</span>
            <textarea rows={3} maxLength={500} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          </label>
          <div className="agenda-form-actions">
            {editingId && <button type="button" className="agenda-button ghost" onClick={reset}>Cancelar edição</button>}
            <button type="submit" className="agenda-button primary" disabled={saving}>{saving ? 'Salvando…' : editingId ? 'Salvar serviço' : 'Adicionar serviço'}</button>
          </div>
        </form>
      </section>

      <section className="agenda-panel">
        <div className="agenda-section-heading">
          <div>
            <span className="agenda-eyebrow"><Clock3 size={15} /> Serviços cadastrados</span>
            <h2>Serviços disponíveis</h2>
            <p>Desativar preserva o histórico e remove a opção de novos agendamentos.</p>
          </div>
        </div>
        <div className="agenda-card-list">
          {services.map((service) => (
            <article key={service.id} className={`agenda-entity-card ${service.active ? '' : 'is-inactive'}`}>
              <span className="agenda-color-dot" style={{ backgroundColor: service.color }} />
              <div className="agenda-entity-main">
                <div className="agenda-entity-title">
                  <strong>{service.name}</strong>
                  {service.isDefault && <span className="agenda-badge">Padrão</span>}
                  {!service.active && <span className="agenda-badge muted">Inativo</span>}
                </div>
                <p>{service.description || 'Sem descrição'}</p>
                <small>{service.durationMinutes} min{service.price !== null ? ` · R$ ${service.price.toFixed(2).replace('.', ',')}` : ''}</small>
              </div>
              <div className="agenda-entity-actions">
                <button type="button" className="agenda-icon-button" onClick={() => startEditing(service)} aria-label={`Editar ${service.name}`}><Pencil size={17} /></button>
                <button type="button" className="agenda-icon-button" onClick={() => setToggleTarget(service)} aria-label={`${service.active ? 'Desativar' : 'Ativar'} ${service.name}`}><Power size={17} /></button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <ConfirmModal
        open={Boolean(toggleTarget)}
        title={toggleTarget?.active ? 'Desativar serviço?' : 'Reativar serviço?'}
        description={toggleTarget?.active
          ? 'Agendamentos existentes serão preservados, mas o serviço deixará de aparecer em novos agendamentos.'
          : 'O serviço voltará a ficar disponível para os profissionais associados.'}
        confirmLabel={toggleTarget?.active ? 'Desativar' : 'Reativar'}
        variant={toggleTarget?.active ? 'destructive' : 'primary'}
        saving={saving}
        onCancel={() => setToggleTarget(null)}
        onConfirm={handleToggle}
      />
    </div>
  );
};

export default ServicesPanel;
